import { useState, type FormEvent } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { useSupabaseQuery } from '../../hooks/useSupabaseQuery';
import { PageHeader } from '../../components/ui/PageHeader';
import { EmptyState } from '../../components/ui/EmptyState';
import type { EsquemaRemuneracionRow } from '../../types/database.types';

const TIPOS = [
  { value: 'salario_fijo', label: 'Salario fijo' },
  { value: 'comision', label: 'Comisión sobre servicios' },
  { value: 'mixto', label: 'Mixto (salario + comisión)' },
] as const;

/** Esquemas de remuneración (DAF v2, sección 21) — se asignan a cada
 * profesional desde su ficha (ProfesionalForm) y son la base que usa
 * `liquidar_comisiones` (supabase/schema_comisiones_pagos.sql) para
 * calcular comisiones. Admin-only, igual que Comisiones. */
export function EsquemasRemuneracion() {
  const { perfil } = useAuth();
  const [mostrarForm, setMostrarForm] = useState(false);

  const { data, loading, error, refetch } = useSupabaseQuery<EsquemaRemuneracionRow[]>(async () => {
    const { data, error } = await supabase.from('esquemas_remuneracion').select('*').eq('estado', 'activo').order('nombre');
    return { data, error };
  });

  function alGuardar() {
    setMostrarForm(false);
    refetch();
  }

  return (
    <div>
      <PageHeader
        title="Esquemas de remuneración"
        description="Salario fijo, comisión o mixto — DAF v2, sección 21"
        actions={
          <button className="btn-primary" onClick={() => setMostrarForm((v) => !v)}>
            {mostrarForm ? 'Cancelar' : 'Nuevo esquema'}
          </button>
        }
      />

      {mostrarForm && perfil && <NuevoEsquemaForm salonId={perfil.salon_id} onGuardado={alGuardar} onCancelar={() => setMostrarForm(false)} />}

      {loading && <p className="text-sm text-onyx/50">Cargando…</p>}
      {error && <p className="text-sm text-red-600">{error}</p>}

      {data && data.length === 0 && <EmptyState title="No hay esquemas de remuneración todavía" />}

      {data && data.length > 0 && (
        <div className="card overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-onyx/10 text-left text-onyx/50">
                <th className="px-4 py-3 font-medium">Nombre</th>
                <th className="px-4 py-3 font-medium">Tipo</th>
                <th className="px-4 py-3 font-medium">Salario base</th>
                <th className="px-4 py-3 font-medium">% Comisión</th>
              </tr>
            </thead>
            <tbody>
              {data.map((e) => (
                <tr key={e.id} className="border-b border-onyx/5 last:border-0">
                  <td className="px-4 py-3 font-medium text-onyx">{e.nombre}</td>
                  <td className="px-4 py-3 text-onyx/70">{TIPOS.find((t) => t.value === e.tipo)?.label ?? e.tipo}</td>
                  <td className="px-4 py-3 text-onyx/70">{e.salario_base != null ? `$${e.salario_base.toLocaleString('es-CO')}` : '—'}</td>
                  <td className="px-4 py-3 text-onyx/70">{e.porcentaje_comision != null ? `${e.porcentaje_comision}%` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function NuevoEsquemaForm({
  salonId,
  onGuardado,
  onCancelar,
}: {
  salonId: string;
  onGuardado: () => void;
  onCancelar: () => void;
}) {
  const [nombre, setNombre] = useState('');
  const [tipo, setTipo] = useState<(typeof TIPOS)[number]['value']>('comision');
  const [salarioBase, setSalarioBase] = useState('');
  const [porcentaje, setPorcentaje] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (!nombre.trim()) {
      setError('Ponle un nombre al esquema');
      return;
    }

    setGuardando(true);
    const { error: err } = await supabase.from('esquemas_remuneracion').insert({
      salon_id: salonId,
      nombre,
      tipo,
      salario_base: tipo === 'comision' ? null : Number(salarioBase) || 0,
      porcentaje_comision: tipo === 'salario_fijo' ? null : Number(porcentaje) || 0,
      estado: 'activo',
    });
    setGuardando(false);

    if (err) {
      setError(err.message);
      return;
    }

    onGuardado();
  }

  return (
    <form onSubmit={handleSubmit} className="card mb-4 space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-onyx/80">Nombre *</span>
          <input required className="input-field" value={nombre} onChange={(e) => setNombre(e.target.value)} />
        </label>
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-onyx/80">Tipo *</span>
          <select className="input-field" value={tipo} onChange={(e) => setTipo(e.target.value as typeof tipo)}>
            {TIPOS.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {tipo !== 'comision' && (
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-onyx/80">Salario base</span>
            <input type="number" min={0} step="0.01" className="input-field" value={salarioBase} onChange={(e) => setSalarioBase(e.target.value)} />
          </label>
        )}
        {tipo !== 'salario_fijo' && (
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-onyx/80">% de comisión</span>
            <input type="number" min={0} max={100} step="0.01" className="input-field" value={porcentaje} onChange={(e) => setPorcentaje(e.target.value)} />
          </label>
        )}
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className="flex gap-3">
        <button type="submit" disabled={guardando} className="btn-primary">
          {guardando ? 'Guardando…' : 'Guardar esquema'}
        </button>
        <button type="button" className="btn-secondary" onClick={onCancelar}>
          Cancelar
        </button>
      </div>
    </form>
  );
}
